/**
 * Hooks para gestión de usuarios del sistema
 * NUEVO MODELO: Usuarios se registran con email
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { userService } from "@/lib/services/users";
import type { SystemUser } from "@/types";

// ==================== QUERY KEYS ====================

export const userKeys = {
  all: ["systemUsers"] as const,
  byStatus: (status: string) => [...userKeys.all, "status", status] as const,
  detail: (id: string) => [...userKeys.all, id] as const,
};

// ==================== QUERY HOOKS ====================

/**
 * Obtiene todos los usuarios del sistema
 */
export function useSystemUsers() {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: () => userService.getAll(),
  });
}

/**
 * Obtiene usuarios por estado
 */
export function useUsersByStatus(
  status: "active" | "inactive" | "pending" | undefined,
) {
  return useQuery({
    queryKey: userKeys.byStatus(status!),
    queryFn: () => userService.getByStatus(status!),
    enabled: !!status,
  });
}

/**
 * Obtiene un usuario por ID
 */
export function useSystemUser(id: string | undefined) {
  return useQuery({
    queryKey: userKeys.detail(id!),
    queryFn: () => userService.getById(id!),
    enabled: !!id,
  });
}

// ==================== MUTATION HOOKS ====================

/**
 * Actualiza un usuario existente
 * En el nuevo modelo, solo se pueden actualizar ciertos campos
 */
export function useUpdateSystemUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
      updatedBy,
    }: {
      userId: string;
      data: Partial<Pick<SystemUser, "displayName" | "phone" | "status">>;
      updatedBy: string;
    }) => {
      return userService.update(userId, data, updatedBy);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.userId),
      });
      toast.success("Usuario actualizado");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar usuario: ${error.message}`);
    },
  });
}

/**
 * Desactiva un usuario
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      updatedBy,
    }: {
      userId: string;
      updatedBy: string;
    }) => {
      return userService.deactivate(userId, updatedBy);
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
      toast.success("Usuario desactivado");
    },
    onError: (error: Error) => {
      toast.error(`Error al desactivar usuario: ${error.message}`);
    },
  });
}

/**
 * Activa un usuario
 */
export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      updatedBy,
    }: {
      userId: string;
      updatedBy: string;
    }) => {
      return userService.activate(userId, updatedBy);
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
      toast.success("Usuario activado");
    },
    onError: (error: Error) => {
      toast.error(`Error al activar usuario: ${error.message}`);
    },
  });
}
